// ============================================================================
// Unit of measure (UoM).
// ----------------------------------------------------------------------------
// REPORTED: "Unit KG."
//
// The app had no unit anywhere: not on the article, not on a document line. An
// article sold by the kilo was billed as a bare quantity, and the FBR payload
// builder fell back to the literal string 'Numbers, pieces, units' for every
// single line because nothing ever set `line.uom`. FBR Digital Invoicing
// validates `uoM` against a controlled list, so "3" of something that is really
// 3 KG is both wrong on the printed invoice and wrong in the filing.
//
// This is a PAKISTAN feature. Spanish invoicing has no unit column and none is
// added: the article form only shows the unit selector, and documents only
// carry `uom`, on a PK account.
//
// ⚠ ENUM VALUES. The strings below are the FBR UoM vocabulary as documented in
// the PRAL "Technical Documentation for DI API" (SRO 69(I)/2025). FBR rejects
// anything outside its own list, so before go-live these must be reconciled
// against the live reference table the seller's FBR account exposes — the same
// open item that already applies to saleType / sroScheduleNo / scenarioId in
// lib/fiscal/pk.js. Values are stored verbatim so a correction here is a
// one-line change, not a data migration.
// ============================================================================

// The default every line starts on — and what pk.js already sent for every
// line before a unit could be chosen, so nothing that exists today moves.
export const DEFAULT_UOM = 'Numbers, pieces, units';

export const UOM_OPTIONS = [
  { value: 'Numbers, pieces, units', slug: 'units' },
  { value: 'KG',                     slug: 'kg' },
  { value: 'Gram',                   slug: 'gram' },
  { value: 'Metric Ton',             slug: 'metricTon' },
  { value: 'Liter',                  slug: 'liter' },
  { value: 'Meter',                  slug: 'meter' },
  { value: 'Square Metre',           slug: 'squareMetre' },
  { value: 'Foot',                   slug: 'foot' },
  { value: 'Square Foot',            slug: 'squareFoot' },
  { value: 'Dozen',                  slug: 'dozen' },
  { value: 'Pair',                   slug: 'pair' },
  { value: 'Packs',                  slug: 'packs' },
  { value: 'Bag',                    slug: 'bag' },
  { value: 'SET',                    slug: 'set' },
  { value: 'Others',                 slug: 'others' },
];

const BY_VALUE = UOM_OPTIONS.reduce((acc, u) => { acc[u.value] = u; return acc; }, {});

/** Is a unit selector shown at all for this country? */
export function uomEnabled(countryCode) {
  return countryCode === 'PK';
}

/** Display label for a stored unit; unknown values print as they are. */
export function uomLabel(value, t) {
  const u = BY_VALUE[value];
  if (!u) return value || '';
  return t ? t(`uom.${u.slug}`, { defaultValue: u.value }) : u.value;
}

/**
 * Short form for the quantity column on a printed document — "3 KG" reads
 * better than "3 Numbers, pieces, units", so the default unit prints as
 * nothing at all.
 */
export function uomShort(value, t) {
  if (!value || value === DEFAULT_UOM) return '';
  return uomLabel(value, t);
}

/** Normalise anything stored/imported to a value we can send to FBR. */
export function normaliseUom(value) {
  const v = String(value || '').trim();
  if (!v) return DEFAULT_UOM;
  const exact = UOM_OPTIONS.find((u) => u.value.toLowerCase() === v.toLowerCase());
  return exact ? exact.value : v;
}

// --- graceful degradation when backend/pk_unit_of_measure.sql is not applied --
// Same pattern the case-price columns use: rather than losing the whole
// document, retry the insert without the unit and tell the user why.

// Matched narrowly: an invoice line has had a unit_price column for years, so
// a plain /unit/ would swallow unrelated failures and retry for nothing.
export const isUomColumnError = (err) => /\buom\b/i.test(err?.message || '');

export const withoutUom = (rows) => rows.map(({ uom, ...rest }) => rest); // eslint-disable-line no-unused-vars
